import { z } from "zod";
import { apiFetch, apiFetchBlob } from "./api";
import * as signalR from "@microsoft/signalr";

// ============================================================================
// DTOs
// ============================================================================

export const BulkStatementAccountFilterDto = z.object({
  branchIds: z.array(z.number()).optional(),
  termsIds: z.array(z.number()).optional(),
  deliveryMethodCodes: z.array(z.string()).optional(),
  onlyHasOpenBalance: z.boolean().optional(),
});
export type BulkStatementAccountFilterDto = z.infer<typeof BulkStatementAccountFilterDto>;

export const BulkStatementRunSummary = z.object({
  runId: z.number(),
  startedAtUtc: z.string(),
  completedAtUtc: z.string().nullable(),
  requestedByUserId: z.string(),
  deliveryMode: z.enum(["Email", "Print", "Both"]),
  status: z.enum(["Pending", "Running", "Completed", "CompletedWithErrors", "Cancelled"]),
  accountCount: z.number(),
  successCount: z.number(),
  failureCount: z.number(),
  skippedCount: z.number(),
  isRecurring: z.boolean(),
});
export type BulkStatementRunSummary = z.infer<typeof BulkStatementRunSummary>;

export const BulkStatementRun = BulkStatementRunSummary.extend({
  notes: z.string().nullable(),
  accountFilter: BulkStatementAccountFilterDto,
});
export type BulkStatementRun = z.infer<typeof BulkStatementRun>;

export const BulkStatementRunItem = z.object({
  runItemId: z.number(),
  runId: z.number(),
  accountId: z.number(),
  accountReferenceNo: z.string().nullable(),
  companyName: z.string().nullable(),
  status: z.enum(["Pending", "Generated", "Delivered", "Failed", "Skipped"]),
  pdfBlobPath: z.string().nullable(),
  emailLogId: z.number().nullable(),
  attemptCount: z.number(),
  lastAttemptUtc: z.string().nullable(),
  errorMessage: z.string().nullable(),
  statementId: z.number().nullable(),
});
export type BulkStatementRunItem = z.infer<typeof BulkStatementRunItem>;

// ============================================================================
// Request / query types
// ============================================================================

export type CreateStatementRunRequest = {
  accountFilter: BulkStatementAccountFilterDto;
  deliveryMode: "Email" | "Print" | "Both";
  notes?: string;
};

export type ListStatementRunsParams = {
  status?: BulkStatementRunSummary["status"];
  from?: string;
  to?: string;
  onlyRecurring?: boolean;
  page?: number;
  pageSize?: number;
};

export type Paged<T> = {
  items: T[];
  page: number;
  pageSize: number;
  totalCount: number;
};

// ============================================================================
// Helpers
// ============================================================================

function qs(params: Record<string, unknown>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    if (Array.isArray(v)) {
      for (const item of v) sp.append(k, String(item));
    } else {
      sp.append(k, String(v));
    }
  }
  return sp.toString();
}

// ============================================================================
// API functions
// ============================================================================

export function createStatementRun(req: CreateStatementRunRequest): Promise<{ runId: number }> {
  return apiFetch<{ runId: number }>("/api/statement-runs", {
    method: "POST",
    body: JSON.stringify(req),
  });
}

export function listStatementRuns(p: ListStatementRunsParams = {}): Promise<Paged<BulkStatementRunSummary>> {
  const search = qs(p as Record<string, unknown>);
  return apiFetch<Paged<BulkStatementRunSummary>>(`/api/statement-runs${search ? `?${search}` : ""}`);
}

export function getStatementRun(runId: number): Promise<BulkStatementRun> {
  return apiFetch<BulkStatementRun>(`/api/statement-runs/${runId}`);
}

export function listStatementRunItems(
  runId: number,
  status?: string,
  page = 1,
  pageSize = 100,
): Promise<Paged<BulkStatementRunItem>> {
  const search = qs({ status, page, pageSize } as Record<string, unknown>);
  return apiFetch<Paged<BulkStatementRunItem>>(`/api/statement-runs/${runId}/items${search ? `?${search}` : ""}`);
}

export function retryFailedStatementRun(runId: number): Promise<void> {
  return apiFetch<void>(`/api/statement-runs/${runId}/retry-failed`, { method: "POST" });
}

export function cancelStatementRun(runId: number): Promise<void> {
  return apiFetch<void>(`/api/statement-runs/${runId}/cancel`, { method: "POST" });
}

export function statementRunPrintTabUrl(runId: number): string {
  return `/api/statement-runs/${runId}/print-tab`;
}

/**
 * Authenticated, browser-safe download of a per-account statement PDF.
 *
 * Uses the internal route GET /api/statement-runs/{runId}/statements/{statementId}/pdf
 * (requires Bearer token — a native `<a href>` cannot carry the JWT header).
 * Mirrors the downloadJobFile pattern: fetch via apiFetchBlob, create an object
 * URL, trigger a synthetic anchor click, then revoke after 60 s.
 */
export async function downloadStatementPdf(runId: number, statementId: number): Promise<void> {
  const blob = await apiFetchBlob(`/api/statement-runs/${runId}/statements/${statementId}/pdf`);
  const objectUrl = URL.createObjectURL(blob);

  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = `statement-${statementId}.pdf`;
  anchor.rel = "noreferrer";
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);

  // Revoke after 60s — the browser needs the URL alive long enough to
  // process the click; immediate revocation can race in some browsers.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
}

// ============================================================================
// SignalR
// ============================================================================

export type RunStatusEvent = {
  runId: number;
  status: BulkStatementRunSummary["status"];
  accountCount: number;
  successCount: number;
  failureCount: number;
  skippedCount: number;
  completedAtUtc: string | null;
};

export type ItemStatusEvent = {
  runItemId: number;
  runId: number;
  accountId: number;
  status: BulkStatementRunItem["status"];
  attemptCount: number;
  errorMessage: string | null;
};

export function buildRunHubConnection(
  getAccessToken: () => Promise<string | null>,
): signalR.HubConnection {
  return new signalR.HubConnectionBuilder()
    .withUrl("/hubs/bulk-statement-runs", {
      accessTokenFactory: async () => (await getAccessToken()) ?? "",
    })
    .withAutomaticReconnect()
    .build();
}
